// Component History tab of the Components item record (H100).
//
// Legacy: mod_parts/includes/tabs/parts_tab_component_history.inc
//   Section 2 "Component History" (:235-290): a Based On filter and the grid
//   Type · Based On · Date · Aircraft REG/ENG No. · A/C@Unit · TSN · TSO · Comment,
//   from `component_history` (parts_ajax_handler.inc:2477-2490).
//   -> GET /v6/items/{id}/component-history[?based_on=]
// This is the install/removal log the earlier lanes looked for in parts_history
// (component-history-rewire-2026-09-19): legacy keeps it in component_history.
// Measured 2026-09-28: 0 rows on peak and g2, 4660 on demo (module_aeronet_demo).
// NOT BUILT (read-only here): add / delete / drag-to-order history rows, the
// Component History Report PDF (:287), and Section 1 "Component Details" — the
// part-level parts_limitations grid (:10-45; 0 rows for flying parts on peak/g2).
import React, { useEffect, useMemo, useState } from "react";
import { MenuItem, TextField, Typography } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";

import { axios } from "../../../api";
import { ContentGridBox } from "../../../components/DatagridProContnet";
import { commonDataGridPremiumSx } from "../../../utils/dataGridSx";

export interface ComponentHistoryRow {
  id: number;
  history_type: string | null;
  type: string | null;
  based_on: string | null;
  record_date: string | null;
  ac_info: string | null;
  ac_unit: number | string | null;
  tsn: number | string | null;
  tso: number | string | null;
  comment: string | null;
}

const columns: GridColDef[] = [
  { field: "history_type", headerName: "Type", flex: 0.8, valueGetter: (p) => p.row.history_type || p.row.type },
  { field: "based_on", headerName: "Based On", flex: 0.8 },
  { field: "record_date", headerName: "Date", flex: 0.8 },
  { field: "ac_info", headerName: "Aircraft REG/ENG No.", flex: 1 },
  { field: "ac_unit", headerName: "A/C@Unit", flex: 0.7 },
  { field: "tsn", headerName: "TSN", flex: 0.6 },
  { field: "tso", headerName: "TSO", flex: 0.6 },
  { field: "comment", headerName: "Comment", flex: 1.6 },
];

const ComponentHistory: React.FC<{ itemId: number }> = ({ itemId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<ComponentHistoryRow[]>([]);
  const [basisOptions, setBasisOptions] = useState<string[]>([]);
  const [basedOn, setBasedOn] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let current = true;
    setLoading(true);
    axios
      .get(`/v6/items/${itemId}/component-history`, {
        params: basedOn ? { based_on: basedOn } : {},
        cache: false,
      })
      .then((resp) => {
        if (!current) return;
        const data: ComponentHistoryRow[] = Array.isArray(resp.data) ? resp.data : [];
        setRows(data);
        if (!basedOn) {
          setBasisOptions(Array.from(new Set(data.map((r) => r.based_on).filter(Boolean) as string[])));
        }
      })
      .catch(() => current && enqueueSnackbar("Error fetching component history", { variant: "error" }))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [basedOn, enqueueSnackbar, itemId]);

  const emptyText = useMemo(
    () => (!loading && rows.length === 0 ? "No History Found" : null),
    [loading, rows.length]
  );

  return (
    <ContentGridBox>
      {basisOptions.length > 0 ? (
        <TextField
          select
          size="small"
          label="Based On"
          value={basedOn}
          onChange={(e) => setBasedOn(e.target.value)}
          sx={{ mb: 2, minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {basisOptions.map((b) => (
            <MenuItem key={b} value={b}>
              {b}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      {emptyText ? (
        <Typography variant="body2" sx={{ mb: 1 }} data-testid="component-history-empty">
          {emptyText}
        </Typography>
      ) : null}
      <DataGridPro
        autoHeight
        density="compact"
        rows={rows}
        columns={columns}
        loading={loading}
        hideFooter={rows.length <= 15}
        sx={{ ...commonDataGridPremiumSx, borderLeft: "none", borderRight: "none" }}
        aria-label="component-history"
      />
    </ContentGridBox>
  );
};

export default ComponentHistory;
